'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Tooltip as ReactTippyTooltip } from 'react-tippy';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import 'react-tippy/dist/tippy.css';

// Create a wrapper component for Tooltip to fix TypeScript issues
const Tooltip = ({ children, ...props }: { children: React.ReactNode } & any) => {
  return <ReactTippyTooltip {...props}>{children}</ReactTippyTooltip>;
};

interface TitleDisplayProps {
  title: string;
  onTitleChange?: (newTitle: string, shouldUpdateHistory?: boolean) => void;
  isEditable?: boolean;
  onEditingStateChange?: (isEditing: boolean) => void;
}

const TitleDisplay: React.FC<TitleDisplayProps> = ({
  title,
  onTitleChange,
  isEditable = false,
  onEditingStateChange
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update editValue when title prop changes (e.g., from AI)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(title);
    }
  }, [title, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEdit = () => {
    setEditValue(title);
    setIsEditing(true);
    onEditingStateChange?.(true);
  };

  const handleSave = () => {
    if (onTitleChange && editValue.trim() !== title) {
      // Pass true to indicate this is a manual change that should be recorded in history
      onTitleChange(editValue.trim(), true);
    }
    setIsEditing(false);
    onEditingStateChange?.(false);
  };

  const handleCancel = () => {
    setEditValue(title);
    setIsEditing(false);
    onEditingStateChange?.(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center space-x-2 flex-1">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="text-2xl font-bold text-gray-800 bg-transparent border-b-2 border-blue-500 focus:outline-none flex-1 min-w-0"
          placeholder="Enter article title..."
        />
        <div className="flex items-center space-x-1">
          <Tooltip title="Save title" position="bottom">
            <button
              onClick={handleSave}
              className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
            >
              <CheckIcon className="h-5 w-5" />
            </button>
          </Tooltip>
          <Tooltip title="Cancel editing" position="bottom">
            <button
              onClick={handleCancel}
              className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="min-w-0 overflow-hidden">
        <Tooltip
          title={title}
          position="bottom"
          trigger="mouseenter"
          arrow={true}
          size="small"
          disabled={(title?.length || 0) < 50} // Only show tooltip for long titles
        >
          <h1 className="text-2xl font-bold text-gray-800 truncate">
            {title || 'Untitled Article'}
          </h1>
        </Tooltip>
      </div>
      {isEditable && (
        <Tooltip title="Edit title" position="bottom">
          <button
            onClick={handleEdit}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default TitleDisplay;
